import { useState, useCallback } from 'react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { IInvoiceLineItem } from '@/@types/invoice';

export interface IInvoiceFormState {
  step: number;
  companyId: Id<'companies'> | null;
  selectedPolicyIds: Set<string>;
  lineItems: IInvoiceLineItem[];
  invoiceNumber: string;
  bankAccountId: Id<'orgBankAccounts'> | null;
  dueDate: string;
  tax: number;
  currency: string;
}

const initialState: IInvoiceFormState = {
  step: 1,
  companyId: null,
  selectedPolicyIds: new Set(),
  lineItems: [],
  invoiceNumber: '',
  bankAccountId: null,
  dueDate: '',
  tax: 0,
  currency: 'EUR',
};

export function useInvoiceForm() {
  const [form, setForm] = useState<IInvoiceFormState>(initialState);

  const setStep = useCallback((step: number) => {
    setForm(prev => ({ ...prev, step }));
  }, []);

  const setCompany = useCallback((companyId: Id<'companies'>) => {
    setForm(prev => ({ ...prev, companyId, selectedPolicyIds: new Set(), lineItems: [] }));
  }, []);

  const togglePolicy = useCallback((policyId: string) => {
    setForm(prev => {
      const next = new Set(prev.selectedPolicyIds);
      if (next.has(policyId)) {
        next.delete(policyId);
      } else {
        next.add(policyId);
      }
      return { ...prev, selectedPolicyIds: next };
    });
  }, []);

  const setLineItems = useCallback((lineItems: IInvoiceLineItem[]) => {
    setForm(prev => ({ ...prev, lineItems }));
  }, []);

  const removeLineItem = useCallback((index: number) => {
    setForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  }, []);

  const setField = useCallback(
    <K extends keyof IInvoiceFormState>(key: K, value: IInvoiceFormState[K]) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const initFromExisting = useCallback(
    (params: {
      companyId: Id<'companies'>;
      lineItems: IInvoiceLineItem[];
      invoiceNumber: string;
      bankAccountId?: Id<'orgBankAccounts'>;
      dueDate?: number;
      tax?: number;
      currency: string;
    }) => {
      setForm({
        step: 3,
        companyId: params.companyId,
        selectedPolicyIds: new Set(params.lineItems.map(li => li.policyId)),
        lineItems: params.lineItems,
        invoiceNumber: params.invoiceNumber,
        bankAccountId: params.bankAccountId ?? null,
        dueDate: params.dueDate ? new Date(params.dueDate).toISOString().split('T')[0] : '',
        tax: params.tax ?? 0,
        currency: params.currency,
      });
    },
    []
  );

  const resetForm = useCallback(() => {
    setForm(initialState);
  }, []);

  return {
    form,
    setStep,
    setCompany,
    togglePolicy,
    setLineItems,
    removeLineItem,
    setField,
    initFromExisting,
    resetForm,
  };
}
